import { useCallback, useEffect, useMemo, useState } from "react"
import { PaginationState } from "@tanstack/react-table"
import { useGetAdvisorSessionsQuery } from "../../../redux/queries"
import { dateRangeToTimestamps } from "../../utils/datetime"
import { AdvisorSession } from "../../../types"
import { DateRangePreset } from "../activity/date-filter"
import { useSubscriptionMap } from "../../hooks/use-subscription-map"
import AdvisorEmptyState from "../advisor-empty-state"
import AdvisorTable from "./table"

const PAGE_SIZE = 25

const AdvisorTab = () => {
  const { data: sessionsPage, isLoading } = useGetAdvisorSessionsQuery({
    page: 1,
    limit: 1000
  })

  const allSessions = useMemo(
    () => sessionsPage?.conversations ?? [],
    [sessionsPage?.conversations]
  )

  const subscriptionMap = useSubscriptionMap(allSessions)

  const [searchQuery, setSearchQuery] = useState("")
  const [datePreset, setDatePreset] = useState<DateRangePreset>("all")
  const [customDateStart, setCustomDateStart] = useState<number | null>(null)
  const [customDateEnd, setCustomDateEnd] = useState<number | null>(null)

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE
  })

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchQuery, datePreset, customDateStart, customDateEnd])

  const handleDateChange = useCallback(
    (range: DateRangePreset, startDate?: Date, endDate?: Date) => {
      if (range === "custom" && startDate && endDate) {
        setDatePreset("custom")
        setCustomDateStart(startDate.getTime())
        setCustomDateEnd(endDate.getTime())
      } else {
        setDatePreset(range)
        setCustomDateStart(null)
        setCustomDateEnd(null)
      }
    },
    []
  )

  const handleClearDate = useCallback(() => {
    setDatePreset("all")
    setCustomDateStart(null)
    setCustomDateEnd(null)
  }, [])

  const filteredSessions = useMemo<AdvisorSession[]>(() => {
    const q = searchQuery.trim().toLowerCase()
    const { min, max } = dateRangeToTimestamps(
      datePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )

    return allSessions.filter(s => {
      if (q) {
        const sub = s.subscriptionId
          ? subscriptionMap[s.subscriptionId]
          : undefined
        // Keep sessions whose subscription hasn't loaded yet — they render a
        // skeleton and will be re-evaluated once the data arrives.
        if (!sub) return true
        const fullName = `${sub.first_name ?? ""} ${
          sub.last_name ?? ""
        }`.toLowerCase()
        if (
          !fullName.includes(q) &&
          !(sub.email ?? "").toLowerCase().includes(q)
        )
          return false
      }

      if (min || max) {
        const t = new Date(s.lastActivityAt).getTime()
        if (min && t < min) return false
        if (max && t > max) return false
      }

      return true
    })
  }, [
    allSessions,
    searchQuery,
    subscriptionMap,
    datePreset,
    customDateStart,
    customDateEnd
  ])

  const paginatedSessions = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize
    return filteredSessions.slice(start, start + pagination.pageSize)
  }, [filteredSessions, pagination.pageIndex, pagination.pageSize])

  const pageCount = Math.ceil(filteredSessions.length / PAGE_SIZE)

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-10 rounded-lg bg-neutral-100 w-full" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-14 rounded-lg bg-neutral-50 border border-neutral-100"
          />
        ))}
      </div>
    )
  }

  if (!allSessions.length) return <AdvisorEmptyState />

  return (
    <AdvisorTable
      sessions={paginatedSessions}
      subscriptionMap={subscriptionMap}
      isLoading={isLoading}
      totalRows={filteredSessions.length}
      pagination={pagination}
      setPagination={setPagination}
      pageCount={pageCount}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      datePreset={datePreset}
      customDateStart={customDateStart}
      customDateEnd={customDateEnd}
      onDateChange={handleDateChange}
      onClearDate={handleClearDate}
    />
  )
}

export default AdvisorTab
